import { Fragment, useEffect, useRef, useState } from "react";
import { Menu, Portal } from "@headlessui/react";
import { Bell, Search, HelpCircle, ChevronDown, Building2, ShieldCheck, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth, type Company } from "../../lib/auth";
import api from "../../lib/api";
import { SearchModal } from "./SearchModal";
import { NotificationsPanel } from "./NotificationsPanel";
import { HelpModal } from "./HelpModal";

const MENU_W = 280;

/** Haal alle bedrijven op die de inlogger mag openen. Superadmin ziet álle
 *  bedrijven (via het beheer-paneel); gewone gebruikers zien hun eigen
 *  bedrijven (al aanwezig op user.companies). */
function useSwitchableCompanies(role?: string): { companies: Company[]; loaded: boolean } {
  const { user } = useAuth();
  const [all, setAll] = useState<Company[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (role !== "SUPERADMIN") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get<Company[]>("/api/superadmin/companies");
        // Normaliseer naar Company-objecten incl. activePackage (uit packages[0])
        // zodat de feature-gating klopt als superadmin naar dit bedrijf wisselt.
        const mapCompany = (c: any): Company => {
          const pkg = c.packages?.[0]?.package;
          return {
            id: c.id,
            name: c.name,
            slug: c.slug,
            logoUrl: c.logoUrl,
            primaryColor: c.primaryColor,
            activePackage: pkg
              ? {
                  key: pkg.key,
                  name: pkg.name,
                  hasQuotes: pkg.hasQuotes,
                  hasExpenses: pkg.hasExpenses,
                  hasPayments: pkg.hasPayments,
                  maxInvoices: pkg.maxInvoices,
                  maxQuotes: pkg.maxQuotes,
                  maxExpenses: pkg.maxExpenses,
                  maxContacts: pkg.maxContacts,
                  hasOcr: pkg.hasOcr,
                  hasVatReport: pkg.hasVatReport,
                }
              : null,
          };
        };
        const list: Company[] = Array.isArray(data)
          ? data.map(mapCompany)
          : Array.isArray((data as any)?.companies)
            ? (data as any).companies.map(mapCompany)
            : [];
        if (alive) {
          setAll(list);
          setLoaded(true);
        }
      } catch {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [role]);

  if (role === "SUPERADMIN") return { companies: all, loaded };
  return { companies: user?.companies ?? [], loaded: true };
}

export function Topbar() {
  const { user, activeCompany, activeCompanyId, setCompany } = useAuth();
  const navigate = useNavigate();
  const { companies, loaded } = useSwitchableCompanies(user?.role);
  const btnRef = useRef<HTMLButtonElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [notifVersion, setNotifVersion] = useState(0);
  const initials = user?.firstName?.[0]?.toUpperCase() || user?.email[0].toUpperCase();

  // Globaal zoeken met Ctrl+K / Cmd+K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Notificatie-badge: laad bij mount + ververs elke 60s. Wissel van bedrijf
  // → opnieuw laden (X-Company-Id verandert).
  useEffect(() => {
    let alive = true;
    const load = () => {
      api
        .get("/api/notifications")
        .then(({ data }) => {
          if (alive) setNotifCount(Number(data?.count) || 0);
        })
        .catch(() => {
          /* badge stil laten falen */
        });
    };
    load();
    const t = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [activeCompanyId, notifVersion]);

  const isSuperadmin = user?.role === "SUPERADMIN";
  const logoSrc = activeCompany?.logoUrl
    ? `${import.meta.env.VITE_API_URL || ""}${activeCompany.logoUrl}`
    : null;
  const displayName = activeCompany?.name || (isSuperadmin ? "Beheerpaneel" : "AIBoek");

  const pickCompany = (c: Company, close: () => void) => {
    close();
    setCompany(c.id, c);
    navigate("/dashboard");
  };

  const goToAdmin = (close: () => void) => {
    close();
    // Wis het bedrijf NIET: het beheerpaneel werkt bedrijfs-onafhankelijk,
    // en zo kan superadmin na het paneel direct weer terug naar het dashboard
    // van het actieve bedrijf (geen valkuil waarbij klikken terug-stuurt).
    navigate("/superadmin");
  };

  return (
    <header className="h-16 sticky top-0 z-30 px-6 flex items-center justify-between">
      <div
        className="absolute inset-0 bg-white/60 backdrop-blur-xl border-b border-white/60"
        style={{ boxShadow: "0 4px 24px -10px rgba(15, 23, 42, 0.06)" }}
      />

      <div className="relative flex items-center gap-3 flex-1">
        <Menu as={Fragment}>
          {({ open, close }) => (
            <div className="relative inline-block">
              <Menu.Button
                ref={btnRef}
                onClick={() => {
                  const r = btnRef.current?.getBoundingClientRect();
                  if (r) {
                    const left = Math.min(r.left, window.innerWidth - MENU_W - 8);
                    setCoords({ top: r.bottom + 6, left: Math.max(8, left) });
                  }
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/70 px-2.5 py-1.5 text-left shadow-sm hover:bg-white hover:border-brand-300 transition-colors max-w-[260px]"
                title="Wissel bedrijf"
              >
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={displayName}
                    className="h-7 w-7 object-contain rounded-md"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <span className="h-7 w-7 rounded-md bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white">
                    {isSuperadmin ? <ShieldCheck size={16} /> : <Building2 size={16} />}
                  </span>
                )}
                <span className="font-bold text-slate-800 text-sm truncate">{displayName}</span>
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
                />
              </Menu.Button>

              {open && (
                <Portal>
                  <Menu.Items
                    style={{ position: "fixed", top: `${coords.top}px`, left: `${coords.left}px`, zIndex: 50 }}
                    className="w-72 rounded-xl border border-slate-200 bg-white py-1 shadow-xl focus:outline-none max-h-80 overflow-y-auto"
                  >
                    {/* Superadmin: terug naar beheerpaneel */}
                    {isSuperadmin && (
                      <>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => goToAdmin(close)}
                              className={`${active ? "bg-brand-50 text-brand-700" : "text-slate-700"} flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left`}
                            >
                              <ShieldCheck size={17} />
                              Beheerpaneel
                            </button>
                          )}
                        </Menu.Item>
                        <div className="my-1 border-t border-slate-100" />
                        <p className="px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
                          Bedrijf openen
                        </p>
                      </>
                    )}

                    {!loaded && (
                      <p className="px-3 py-2 text-sm text-slate-400">Laden…</p>
                    )}

                    {loaded && companies.length === 0 && !isSuperadmin && (
                      <p className="px-3 py-2 text-sm text-slate-400">Geen bedrijven</p>
                    )}

                    {loaded && companies.map((c) => {
                      const isActive = c.id === activeCompanyId;
                      const cLogo = c.logoUrl
                        ? `${import.meta.env.VITE_API_URL || ""}${c.logoUrl}`
                        : null;
                      return (
                        <Menu.Item key={c.id}>
                          {({ active }) => (
                            <button
                              onClick={() => pickCompany(c, close)}
                              className={`${active ? "bg-brand-50" : ""} ${isActive ? "text-brand-700" : "text-slate-700"} flex w-full items-center gap-2.5 px-3 py-2 text-sm text-left`}
                            >
                              {cLogo ? (
                                <img src={cLogo} alt={c.name} className="h-6 w-6 object-contain rounded-md" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                              ) : (
                                <span className="h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center text-slate-500">
                                  <Building2 size={14} />
                                </span>
                              )}
                              <span className="truncate flex-1">{c.name}</span>
                              {isActive && <Check size={16} className="text-brand-600 shrink-0" />}
                            </button>
                          )}
                        </Menu.Item>
                      );
                    })}
                  </Menu.Items>
                </Portal>
              )}
            </div>
          )}
        </Menu>
      </div>

      <div className="relative flex items-center gap-2">
        <button
          onClick={() => setSearchOpen(true)}
          title="Zoeken (Ctrl+K)"
          className="p-2 rounded-xl text-slate-500 hover:bg-white/70 hover:text-slate-700 transition-colors"
        >
          <Search size={30} />
        </button>
        <button
          ref={bellRef}
          onClick={() => setNotifOpen((o) => !o)}
          title="Meldingen"
          className="relative p-2 rounded-xl text-slate-500 hover:bg-white/70 hover:text-slate-700 transition-colors"
        >
          <Bell size={30} />
          {notifCount > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow-sm"
              title={`${notifCount} aandachtspunt(en)`}
            >
              {notifCount > 99 ? "99+" : notifCount}
            </span>
          )}
        </button>
        <button
          onClick={() => setHelpOpen(true)}
          title="Hulp & snelstart"
          className="p-2 rounded-xl text-slate-500 hover:bg-white/70 hover:text-slate-700 transition-colors"
        >
          <HelpCircle size={30} />
        </button>

        <div className="flex items-center gap-3 pl-3 ml-1 border-l border-slate-200/80">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-400 to-indigo-500 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-brand-500/20">
            {initials}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-bold text-slate-800">{user?.firstName || user?.email}</p>
            <p className="text-xs text-slate-500">{isSuperadmin ? "Superadmin" : "Gebruiker"}</p>
          </div>
        </div>
      </div>

      {/* Globaal zoeken (Ctrl+K) */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Notificatie-paneel onder de bel */}
      <NotificationsPanel
        count={notifCount}
        open={notifOpen}
        anchorRef={bellRef}
        onClose={() => {
          setNotifOpen(false);
          setNotifVersion((v) => v + 1);
        }}
        onOpened={() => setNotifVersion((v) => v + 1)}
      />

      {/* Help & snelstart */}
      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}